import React, { useState, useEffect, useRef } from 'react';
import {
  Card,
  Typography,
  Row,
  Col,
  Statistic,
  Select,
  DatePicker,
  message,
  Empty,
  Spin,
  Alert,
  Progress,
  Space,
  Divider,
  Table,
  Tag,
  Tooltip,
  Switch
} from 'antd';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import {
  BarChartOutlined,
  PieChartOutlined,
  LineChartOutlined,
  DashboardOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  ClockCircleOutlined,
  ThunderboltOutlined,
  StarOutlined
} from '@ant-design/icons';
import { analyticsAPI } from '../services/api';
import LoadingAnimation from './LoadingAnimation';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
  ArcElement,
} from 'chart.js';
import { Bar, Line, Doughnut } from 'react-chartjs-2';

const { Title: AntTitle, Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  ChartTooltip,
  Legend,
  ArcElement
);

interface AnalyticsData {
  application_id: string;
  summary: {
    total_requests: number;
    success_count: number;
    failure_count: number;
    success_rate: number;
    avg_processing_time: number;
    avg_confidence_score: number;
    total_processing_time: number;
    date_range: {
      start: string;
      end: string;
      filter: string;
    };
  };
  performance_metrics: {
    fastest_request: {
      time: number;
      request_id: string;
    };
    slowest_request: {
      time: number;
      request_id: string;
    };
    highest_confidence: {
      score: number;
      request_id: string;
    };
    lowest_confidence: {
      score: number;
      request_id: string;
    };
    peak_hour: {
      hour: string;
      requests: number;
    };
    peak_day: {
      date: string;
      requests: number;
    };
    quality_score: number;
  };
  charts: {
    hourly_distribution: Array<{ hour: string; requests: number }>;
    document_types: Array<{ name: string; value: number }>;
    status_breakdown: Array<{ name: string; value: number; color: string }>;
    confidence_over_time: Array<{ date: string; avg_confidence: number; min_confidence: number; max_confidence: number }>;
    processing_time_over_time: Array<{ date: string; avg_time: number; min_time: number; max_time: number }>;
    weekly_pattern: Array<{ day: string; requests: number }>;
  };
  trends: {
    performance_trend: Array<{ date: string; avg_processing_time: number; request_count: number }>;
    quality_trend: Array<{ date: string; success_rate: number; avg_confidence: number }>;
    volume_trend: Array<{ date: string; volume: number }>;
  };
  recent_requests: Array<{
    id: string;
    document_type: string;
    status: string;
    confidence_score: number;
    processing_time_ms: number;
    created_at: string;
    error_message: string | null;
  }>;
}

interface ApplicationAnalyticsProps {
  applicationId: string;
  applicationName?: string;
  showTitle?: boolean;
}

const ApplicationAnalytics: React.FC<ApplicationAnalyticsProps> = ({ 
  applicationId, 
  applicationName,
  showTitle = true 
}) => {
  const [loading, setLoading] = useState(true);
  const [analyticsData, setAnalyticsData] = useState<AnalyticsData | null>(null);
  const [dateFilter, setDateFilter] = useState<string>('7d');
  const [refreshing, setRefreshing] = useState(false);
  
  // Custom date range states
  const [useCustomDateRange, setUseCustomDateRange] = useState(false);
  const [customDateRange, setCustomDateRange] = useState<[Dayjs, Dayjs] | null>(null);
  const loadingRef = useRef(false);

  useEffect(() => {
    if (loadingRef.current) return;
    loadAnalyticsData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicationId, dateFilter, useCustomDateRange, customDateRange]);

  const loadAnalyticsData = async () => {
    if (loadingRef.current) return;
    
    try {
      loadingRef.current = true;
      setLoading(true);
      console.log(`Loading analytics for application ${applicationId}`, { 
        useCustomDateRange, 
        customDateRange, 
        dateFilter 
      });
      
      let params: any = {
        format: 'detailed'
      };
      
      if (useCustomDateRange && customDateRange) {
        // Use custom date range
        params.date_filter = 'custom';
        params.start_date = customDateRange[0].toISOString();
        params.end_date = customDateRange[1].toISOString();
      } else {
        // Use predefined date filter
        params.date_filter = dateFilter;
      }
      
      const response = await analyticsAPI.getApplicationAnalytics(applicationId, params);
      
      console.log('Application analytics response:', response.data);
      
      // Handle both direct data and nested data structure
      if (response.data.success && response.data.data) {
        setAnalyticsData(response.data.data);
      } else if (response.data.summary) {
        // If response has direct analytics data
        setAnalyticsData(response.data);
      } else {
        console.warn('Unexpected API response structure:', response.data);
        setAnalyticsData(null);
      }
    } catch (error: any) {
      console.error('Error loading application analytics:', error);
      message.error('Failed to load analytics data');
      setAnalyticsData(null);
    } finally {
      setLoading(false);
      setRefreshing(false);
      loadingRef.current = false;
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    loadAnalyticsData();
  };

  if (loading && !refreshing) {
    return (
      <LoadingAnimation
        type="orbit"
        message="Loading analytics data..."
        size="large"
      />
    );
  }

  if (!analyticsData || (!analyticsData.summary && !analyticsData.charts)) {
    return (
      <div style={{ padding: '20px' }}>
        {/* Date Filter Controls for Empty State */}
        <Card size="small" style={{ marginBottom: '16px' }}>
          <Row gutter={[16, 16]} align="middle">
            <Col xs={24} sm={8} md={6}>
              <div>
                <Text strong style={{ marginRight: 8 }}>Custom Dates:</Text>
                <Switch
                  checked={useCustomDateRange}
                  onChange={(checked) => {
                    setUseCustomDateRange(checked);
                    if (!checked) {
                      setCustomDateRange(null);
                    }
                  }}
                  checkedChildren="Custom"
                  unCheckedChildren="Preset"
                  size="small"
                />
              </div>
            </Col>
            
            <Col xs={24} sm={useCustomDateRange ? 12 : 8} md={useCustomDateRange ? 12 : 6}>
              {useCustomDateRange ? (
                <div>
                  <Text strong style={{ marginRight: 8 }}>Date Range:</Text>
                  <RangePicker
                    value={customDateRange}
                    onChange={(dates) => {
                      if (dates && dates[0] && dates[1]) {
                        setCustomDateRange([dates[0], dates[1]]);
                      } else {
                        setCustomDateRange(null);
                      }
                    }}
                    style={{ width: '100%' }}
                    format="YYYY-MM-DD"
                    allowClear
                    placeholder={['Start Date', 'End Date']}
                    disabledDate={(current) => {
                      // Disable future dates
                      return current && current > dayjs().endOf('day');
                    }}
                    size="small"
                  />
                </div>
              ) : (
                <div>
                  <Text strong style={{ marginRight: 8 }}>Time Period:</Text>
                  <Select
                    value={dateFilter}
                    onChange={setDateFilter}
                    style={{ width: '100%' }}
                    size="small"
                  >
                    <Option value="1d">Last Day</Option>
                    <Option value="7d">Last Week</Option>
                    <Option value="30d">Last Month</Option>
                    <Option value="90d">Last 3 Months</Option>
                  </Select>
                </div>
              )}
            </Col>
            
            <Col xs={24} sm={8} md={6}>
              <Tooltip title="Refresh Data">
                <button 
                  onClick={handleRefresh}
                  disabled={refreshing}
                  style={{
                    background: 'none',
                    border: '1px solid #d9d9d9',
                    borderRadius: '6px',
                    padding: '4px 15px',
                    cursor: refreshing ? 'not-allowed' : 'pointer',
                    color: refreshing ? '#bfbfbf' : '#1890ff',
                    transition: 'all 0.3s ease'
                  }}
                >
                  {refreshing ? (
                    <LoadingAnimation type="pulse" size="small" />
                  ) : (
                    '↻'
                  )}
                </button>
              </Tooltip>
            </Col>
          </Row>
          
          {/* Show current filter info */}
          <Row style={{ marginTop: 12 }}>
            <Col span={24}>
              <Alert
                message={
                  useCustomDateRange && customDateRange
                    ? `Custom date range: ${customDateRange[0].format('YYYY-MM-DD')} to ${customDateRange[1].format('YYYY-MM-DD')}`
                    : `Showing ${dateFilter} data for this application`
                }
                type="info"
                showIcon
                style={{ backgroundColor: '#f6ffed', border: '1px solid #b7eb8f', fontSize: '12px' }}
              />
            </Col>
          </Row>
        </Card>
        
        <div style={{ padding: '50px', textAlign: 'center' }}>
          <Empty
            description="No analytics data available for this application"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          >
            <p style={{ color: '#8c8c8c', margin: '16px 0' }}>
              Analytics data will appear here once OCR requests have been processed for this application.
              Try adjusting the date range above or check if there are any requests for this time period.
            </p>
          </Empty>
        </div>
      </div>
    );
  }

  const { summary, charts, performance_metrics, recent_requests } = analyticsData;
  
  // Add defensive checks for undefined objects
  const safeCharts = charts || {};
  const safeSummary = summary || {};
  const safePerformanceMetrics = performance_metrics || {};
  const safeRecentRequests = recent_requests || [];

  // Chart configurations
  const statusBreakdownData = {
    labels: (safeCharts.status_breakdown || []).map(item => item.name || 'Unknown'),
    datasets: [{
      data: (safeCharts.status_breakdown || []).map(item => item.value || 0),
      backgroundColor: (safeCharts.status_breakdown || []).map(item => item.color || '#1890ff'),
      borderWidth: 2,
    }]
  };

  const documentTypesData = {
    labels: (safeCharts.document_types || []).map(item => item.name || 'Unknown'),
    datasets: [{
      data: (safeCharts.document_types || []).map(item => item.value || 0),
      backgroundColor: [
        '#1890ff', '#52c41a', '#faad14', '#f759ab', '#13c2c2', 
        '#722ed1', '#fa541c', '#2f54eb', '#a0d911', '#eb2f96'
      ],
      borderWidth: 2,
    }]
  };

  const processingTimeTrendsData = {
    labels: (safeCharts.processing_time_over_time || []).map(item => item.date || ''),
    datasets: [
      {
        label: 'Average Processing Time',
        data: (safeCharts.processing_time_over_time || []).map(item => item.avg_time || 0),
        borderColor: '#1890ff',
        backgroundColor: 'rgba(24, 144, 255, 0.1)',
        fill: true,
      },
      {
        label: 'Min Processing Time',
        data: (safeCharts.processing_time_over_time || []).map(item => item.min_time || 0),
        borderColor: '#52c41a',
        backgroundColor: 'rgba(82, 196, 26, 0.1)',
        fill: false,
      },
      {
        label: 'Max Processing Time',
        data: (safeCharts.processing_time_over_time || []).map(item => item.max_time || 0),
        borderColor: '#ff4d4f',
        backgroundColor: 'rgba(255, 77, 79, 0.1)',
        fill: false,
      }
    ]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
      },
    },
  };

  const latestRequestsColumns = [
    {
      title: 'Timestamp',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (timestamp: string) => new Date(timestamp).toLocaleString(),
      sorter: (a: any, b: any) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
    },
    {
      title: 'Document Type',
      dataIndex: 'document_type',
      key: 'document_type',
      render: (type: string) => <Tag color="blue">{type || 'Unknown'}</Tag>,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={status === 'completed' ? 'success' : status === 'failed' ? 'error' : 'processing'}>
          {status?.toUpperCase() || 'UNKNOWN'}
        </Tag>
      ),
    },
    {
      title: 'Processing Time',
      dataIndex: 'processing_time_ms',
      key: 'processing_time_ms',
      render: (time: number) => `${time ? Math.round(time) : 0}ms`,
      sorter: (a: any, b: any) => (a.processing_time_ms || 0) - (b.processing_time_ms || 0),
    },
    {
      title: 'Confidence',
      dataIndex: 'confidence_score',
      key: 'confidence_score',
      render: (score: number) => (
        <Tooltip title={`${((score || 0) * 100).toFixed(1)}% confidence`}>
          <Progress 
            percent={((score || 0) * 100)} 
            size="small" 
            strokeColor={score >= 0.8 ? '#52c41a' : score >= 0.6 ? '#faad14' : '#ff4d4f'} 
          />
        </Tooltip>
      ),
      sorter: (a: any, b: any) => (a.confidence_score || 0) - (b.confidence_score || 0),
    }
  ];

  return (
    <div style={{ padding: '0' }}>
      {/* Date Filter Controls - Always show these */}
      <Card size="small" style={{ marginBottom: '16px' }}>
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} sm={8} md={6}>
            <div>
              <Text strong style={{ marginRight: 8 }}>Custom Dates:</Text>
              <Switch
                checked={useCustomDateRange}
                onChange={(checked) => {
                  setUseCustomDateRange(checked);
                  if (!checked) {
                    setCustomDateRange(null);
                  }
                }}
                checkedChildren="Custom"
                unCheckedChildren="Preset"
                size="small"
              />
            </div>
          </Col>
          
          <Col xs={24} sm={useCustomDateRange ? 12 : 8} md={useCustomDateRange ? 12 : 6}>
            {useCustomDateRange ? (
              <div>
                <Text strong style={{ marginRight: 8 }}>Date Range:</Text>
                <RangePicker
                  value={customDateRange}
                  onChange={(dates) => {
                    if (dates && dates[0] && dates[1]) {
                      setCustomDateRange([dates[0], dates[1]]);
                    } else {
                      setCustomDateRange(null);
                    }
                  }}
                  style={{ width: '100%' }}
                  format="YYYY-MM-DD"
                  allowClear
                  placeholder={['Start Date', 'End Date']}
                  disabledDate={(current) => {
                    // Disable future dates
                    return current && current > dayjs().endOf('day');
                  }}
                  size="small"
                />
              </div>
            ) : (
              <div>
                <Text strong style={{ marginRight: 8 }}>Time Period:</Text>
                <Select
                  value={dateFilter}
                  onChange={setDateFilter}
                  style={{ width: '100%' }}
                  size="small"
                >
                  <Option value="1d">Last Day</Option>
                  <Option value="7d">Last Week</Option>
                  <Option value="30d">Last Month</Option>
                  <Option value="90d">Last 3 Months</Option>
                </Select>
              </div>
            )}
          </Col>
          
          <Col xs={24} sm={8} md={6}>
            <Space>
              <Tooltip title="Refresh Data">
                <button 
                  onClick={handleRefresh}
                  disabled={refreshing}
                  style={{
                    background: 'none',
                    border: '1px solid #d9d9d9',
                    borderRadius: '6px',
                    padding: '4px 15px',
                    cursor: refreshing ? 'not-allowed' : 'pointer',
                    color: refreshing ? '#bfbfbf' : '#1890ff',
                    transition: 'all 0.3s ease'
                  }}
                >
                  {refreshing ? (
                    <LoadingAnimation type="pulse" size="small" />
                  ) : (
                    '↻'
                  )}
                </button>
              </Tooltip>
            </Space>
          </Col>
        </Row>
        
        {/* Show current filter info */}
        <Row style={{ marginTop: 12 }}>
          <Col span={24}>
            <Alert
              message={
                useCustomDateRange && customDateRange
                  ? `Custom date range: ${customDateRange[0].format('YYYY-MM-DD')} to ${customDateRange[1].format('YYYY-MM-DD')}`
                  : `Showing ${dateFilter} data for this application`
              }
              type="info"
              showIcon
              style={{ backgroundColor: '#f6ffed', border: '1px solid #b7eb8f', fontSize: '12px' }}
            />
          </Col>
        </Row>
      </Card>
      
      {showTitle && (
        <div style={{ marginBottom: '24px' }}>
          <AntTitle level={3} style={{ margin: 0 }}>
            <BarChartOutlined /> Analytics{applicationName ? ` - ${applicationName}` : ''}
          </AntTitle>
        </div>
      )}

      {/* Summary Statistics */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Total Requests"
              value={safeSummary?.total_requests || 0}
              prefix={<DashboardOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Success Rate"
              value={safeSummary?.success_rate || 0}
              precision={1}
              suffix="%"
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: safeSummary?.success_rate >= 90 ? '#3f8600' : '#cf1322' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Avg Processing Time"
              value={safeSummary?.avg_processing_time || 0}
              precision={0}
              suffix="ms"
              prefix={<ClockCircleOutlined />}
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Document Types"
              value={(safeCharts?.document_types || []).length}
              prefix={<StarOutlined />}
              valueStyle={{ color: '#fa8c16' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Performance Metrics */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Fastest Request"
              value={safePerformanceMetrics?.fastest_request?.time || 0}
              precision={0}
              suffix="ms"
              prefix={<ThunderboltOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Slowest Request"
              value={safePerformanceMetrics?.slowest_request?.time || 0}
              precision={0}
              suffix="ms"
              prefix={<ExclamationCircleOutlined />}
              valueStyle={{ color: '#ff4d4f' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Peak Hour"
              value={safePerformanceMetrics?.peak_hour?.hour || 'N/A'}
              prefix={<ArrowUpOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Quality Score"
              value={safePerformanceMetrics?.quality_score || 0}
              precision={1}
              suffix="%"
              valueStyle={{ 
                color: safePerformanceMetrics?.quality_score >= 80 ? '#3f8600' : 
                       safePerformanceMetrics?.quality_score >= 60 ? '#faad14' : '#cf1322' 
              }}
            />
          </Card>
        </Col>
      </Row>

      {/* Charts */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} lg={8}>
          <Card title="Request Status Distribution" style={{ height: '400px' }}>
            <div style={{ height: '300px' }}>
              {safeCharts?.status_breakdown?.length > 0 ? (
                <Doughnut data={statusBreakdownData} options={chartOptions} />
              ) : (
                <Empty description="No status data" />
              )}
            </div>
          </Card>
        </Col>
        <Col xs={24} lg={8}>
          <Card title="Document Types" style={{ height: '400px' }}>
            <div style={{ height: '300px' }}>
              {safeCharts?.document_types?.length > 0 ? (
                <Doughnut data={documentTypesData} options={chartOptions} />
              ) : (
                <Empty description="No document type data" />
              )}
            </div>
          </Card>
        </Col>
        <Col xs={24} lg={8}>
          <Card title="Processing Time Trends" style={{ height: '400px' }}>
            <div style={{ height: '300px' }}>
              {safeCharts?.processing_time_over_time?.length > 0 ? (
                <Line data={processingTimeTrendsData} options={chartOptions} />
              ) : (
                <Empty description="No trend data" />
              )}
            </div>
          </Card>
        </Col>
      </Row>

      {/* Latest Requests Table */}
      <Card title="Recent Requests" style={{ marginBottom: '24px' }}>
        <Table
          dataSource={safeRecentRequests || []}
          columns={latestRequestsColumns}
          rowKey="id"
          pagination={{ 
            pageSize: 10,
            showSizeChanger: false,
            showQuickJumper: true 
          }}
          scroll={{ x: 800 }}
        />
      </Card>
    </div>
  );
};

export default ApplicationAnalytics;
